import React, { useState } from 'react';
import COLORS from '@/resources/style/Themes';
import { Box, Button, Collapse, Typography } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import PillRowSelector from './PillRowSelector';
import SmartAdaptiveRangeSlider from './SmartAdaptiveRangeSlider';
import type { RangeThumb } from '@/hooks/pvd_config_hooks/usePvdConfigFields';

type Channel = 'voltage' | 'current';

interface Props {
  channel: Channel;

  // Bubble Size three-state for THIS channel
  bubbleSizeIsLocked: boolean;
  bubbleSizeLockedToValue: boolean;
  setBubbleUserDefined: () => void;
  setBubbleSmall: () => void;
  setBubbleLarge: () => void;

  // Auto-Adapt three-state (shared across both channels — the parent passes
  // the same setters in for both expansions; per spec §4.4 the two
  // expansions render their own selector but mutate the same fields).
  autoAdaptIsLocked: boolean;
  autoAdaptLockedToValue: boolean;
  setAutoAdaptUserDefined: () => void;
  setAutoAdaptOn: () => void;
  setAutoAdaptOff: () => void;

  // SA dual-thumb range for THIS channel
  channelMin: number;
  channelMax: number;
  minValue: number;
  maxValue: number;
  onRangeChange: (min: number, max: number, activeThumb: RangeThumb) => void;
}

/**
 * Three Bubble Size states resolve to a pair of bools (Spec D18):
 *   User Defined -> is_locked = false (locked_to_value irrelevant on the wire)
 *   Small        -> is_locked = true,  locked_to_value = false
 *   Large        -> is_locked = true,  locked_to_value = true
 * No future-firmware fall-back: the wire type is now bool, so values outside
 * {true, false} are unrepresentable. If firmware ever re-extends the value
 * space, a future schema rev re-introduces a wider type and this component
 * grows back its fourth-pill renderer.
 */

const SmartAdaptiveExpansion: React.FC<Props> = ({
  channel,
  bubbleSizeIsLocked,
  bubbleSizeLockedToValue,
  setBubbleUserDefined,
  setBubbleSmall,
  setBubbleLarge,
  autoAdaptIsLocked,
  autoAdaptLockedToValue,
  setAutoAdaptUserDefined,
  setAutoAdaptOn,
  setAutoAdaptOff,
  channelMin,
  channelMax,
  minValue,
  maxValue,
  onRangeChange,
}) => {
  const { t } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const bubbleSelected: string = !bubbleSizeIsLocked
    ? 'USER_DEFINED'
    : bubbleSizeLockedToValue
      ? 'LARGE'
      : 'SMALL';

  const bubbleOptions: { value: string; labelKey: string }[] = [
    { value: 'USER_DEFINED', labelKey: t(StringKeys.BUBBLE_SIZE_USER_DEFINED) },
    { value: 'SMALL', labelKey: t(StringKeys.BUBBLE_SIZE_SMALL) },
    { value: 'LARGE', labelKey: t(StringKeys.BUBBLE_SIZE_LARGE) },
  ];

  const handleBubbleSelect = (value: string) => {
    if (value === 'USER_DEFINED') setBubbleUserDefined();
    else if (value === 'SMALL') setBubbleSmall();
    else if (value === 'LARGE') setBubbleLarge();
  };

  const autoAdaptSelected: string = !autoAdaptIsLocked
    ? 'USER_DEFINED'
    : autoAdaptLockedToValue
      ? 'ON'
      : 'OFF';

  const autoAdaptOptions = [
    { value: 'USER_DEFINED', labelKey: t(StringKeys.AUTO_ADAPT_USER_DEFINED) },
    { value: 'ON', labelKey: t(StringKeys.SMART_ADAPTIVE_ONLY) },
    { value: 'OFF', labelKey: t(StringKeys.WORK_ZONE_ADAPT_ONLY) },
  ];

  const handleAutoAdaptSelect = (value: string) => {
    if (value === 'USER_DEFINED') setAutoAdaptUserDefined();
    else if (value === 'ON') setAutoAdaptOn();
    else if (value === 'OFF') setAutoAdaptOff();
  };

  return (
    <Box sx={{ mt: 2, pl: 2, borderLeft: (theme) => `2px solid ${theme.palette.grey[300]}` }}>
      <PillRowSelector
        label={t(StringKeys.ADAPTIVE_MODES)}
        options={autoAdaptOptions}
        selectedValue={autoAdaptSelected}
        onSelect={handleAutoAdaptSelect}
        captionKey={t(StringKeys.AUTO_ADAPT_APPLIES_TO_BOTH)}
      />

      <Button
        variant="text"
        size="small"
        onClick={() => setAdvancedOpen((open) => !open)}
        startIcon={advancedOpen ? <ExpandLess /> : <ExpandMore />}
        sx={{ mt: 2, color: COLORS.TEXT_HEADER_GRAY }}
        data-testid={`sa-advanced-toggle-${channel}`}
      >
        {t(StringKeys.ADVANCED_SETTINGS)}
      </Button>

      <Collapse in={advancedOpen} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <PillRowSelector
            label={t(StringKeys.WORK_AREA_SIZE)}
            options={bubbleOptions}
            selectedValue={bubbleSelected}
            onSelect={handleBubbleSelect}
          />

          <SmartAdaptiveRangeSlider
            channelMin={channelMin}
            channelMax={channelMax}
            minValue={minValue}
            maxValue={maxValue}
            onChange={onRangeChange}
          />
        </Box>
      </Collapse>

      {/* Reference channel so it counts as used (TypeScript noUnusedParameters). */}
      <Typography component="span" hidden>{channel}</Typography>
    </Box>
  );
};

export default SmartAdaptiveExpansion;
